import { Routes } from '@packages/enums';
import { Icon } from '@ui/assets/icons/IconWrapper';
import { Button } from '@ui/components/button';
import { logger } from '@ui/utils/logger';
import { Checkbox, Form, InputNumber, Input } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router';
import PatientListSelection from '@ui/features/Cohort/components/CohortForm/PatientListSelection';
import {
    CohortFormProvider,
    useCohortForm,
    useCohortPatientData,
} from '@ui/features/Cohort/components/CohortForm/state';
import { useCohortMessageId } from '@ui/features/Cohort/hooks/useCohortMessageId';
import { useGetAllCohortsQuery, useUpdateCohortMutation } from '@ui/features/Cohort/service/api';
import { notification } from 'antd';
import type { AdvancedFilterQuery } from '@packages/types';

/**
 * Edit Cohort Form Content - Inner component that uses CohortForm state
 */
function EditCohortFormContent() {
    const { cohort_id } = useParams<{ cohort_id: string }>();
    const navigate = useNavigate();
    const { form } = useCohortForm();
    const { patientsData, unfilteredPatientsData, loadingPatients } = useCohortPatientData();
    const [updateCohort, { isLoading: updatingCohort }] = useUpdateCohortMutation();

    const { data: cohortsData, isFetching: loadingCohort } = useGetAllCohortsQuery();

    // Find current cohort
    const currentCohort = useMemo(() => {
        if (!cohortsData?.cohorts || !cohort_id) return null;
        return cohortsData.cohorts.find(c => c.cohort_id === cohort_id);
    }, [cohortsData, cohort_id]);

    // Track if filters have been loaded to prevent re-setting
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    // Track initial filter to detect changes
    const initialFilterRef = useRef<AdvancedFilterQuery | undefined>(undefined);
    // Track if filter has been modified (to uncheck random selection)
    const [filterModified, setFilterModified] = useState(false);
    // Track filtered patient count (before random selection) for max sample size
    const [filteredPatientCount, setFilteredPatientCount] = useState<number>(0);
    // Track last random seed to avoid unnecessary updates
    const lastRandomSeedRef = useRef<number | null>(null);
    // Track excluded patient IDs (unchecked checkboxes); string[] to support "es-only:xxx" ids
    const [excludedPatientIds, setExcludedPatientIds] = useState<string[]>([]);
    // Cohort name editing state
    const [isEditingName, setIsEditingName] = useState(false);
    const [cohortName, setCohortName] = useState<string>('');

    // Watch for filter changes to uncheck random selection
    const currentFilter = Form.useWatch('patientAdvancedFilter', form);
    const watchedIncludeEs = Form.useWatch('includeElasticsearchForFilters', form);
    useEffect(() => {
        queueMicrotask(() => {
            if (!filtersLoaded) return; // Don't check until initial load is complete

            // Compare current filter with initial filter (excluding excludedPatientIds for comparison)
            const currentFilterForComparison = currentFilter
                ? (() => {
                      const { excludedPatientIds: _, ...rest } = currentFilter as any;
                      return Object.keys(rest).length > 0 ? rest : undefined;
                  })()
                : undefined;

            const initialFilterForComparison = initialFilterRef.current
                ? (() => {
                      const { excludedPatientIds: _, ...rest } = initialFilterRef.current as any;
                      return Object.keys(rest).length > 0 ? rest : undefined;
                  })()
                : undefined;

            // Check if filter has changed (deep comparison would be better, but this should work for most cases)
            const filterChanged =
                JSON.stringify(currentFilterForComparison) !== JSON.stringify(initialFilterForComparison);

            if (filterChanged && !filterModified) {
                setFilterModified(true);
                // Uncheck random selection when filter changes
                form.setFieldsValue({ randomEnabled: false, randomCount: undefined });
                // Reset excluded patient IDs when filter changes
                setExcludedPatientIds([]);
            }
        });
    }, [currentFilter, filtersLoaded, filterModified, form]);

    // Sync excludedPatientIds from form when form has them but state is still empty (handles initial load race)
    useEffect(() => {
        if (!filtersLoaded) return;
        const formFilter = form?.getFieldValue('patientAdvancedFilter');
        const formExcluded = formFilter?.excludedPatientIds;
        if (Array.isArray(formExcluded) && formExcluded.length > 0 && excludedPatientIds.length === 0) {
            const ids = formExcluded.map((id: string | number) => String(id));
            queueMicrotask(() => setExcludedPatientIds(ids));
        }
    }, [filtersLoaded, form, excludedPatientIds.length]);

    // Load cohort filters into form when cohort is loaded (only once on mount)
    useEffect(() => {
        queueMicrotask(() => {
            if (!currentCohort || !form || filtersLoaded) return;

            // Set all form values at once
            const formValues: any = {};

            // Parse filter_criteria from cohort (inline to avoid dependency issues)
            let parsedFilterValue: AdvancedFilterQuery | undefined = undefined;
            let excludedPatientIds: string[] | undefined = undefined;

            if (currentCohort.filter_criteria) {
                try {
                    const filterCriteria =
                        typeof currentCohort.filter_criteria === 'string'
                            ? JSON.parse(currentCohort.filter_criteria)
                            : currentCohort.filter_criteria;

                    if (filterCriteria) {
                        // Handle wrapped filter structure ({ mode: "standard", filter: {...} })
                        let standardFilter = filterCriteria;
                        if (filterCriteria.filter) {
                            // Extract the inner filter if wrapped
                            standardFilter = filterCriteria.filter;
                        }

                        // Restore Silver+Bronze switch from filter_criteria
                        if (filterCriteria.include_elasticsearch_for_filters === true) {
                            formValues.includeElasticsearchForFilters = true;
                        }

                        // Extract excludedPatientIds separately
                        excludedPatientIds = standardFilter.excludedPatientIds;

                        // Remove excludedPatientIds from filter (will be added back to patientAdvancedFilter)
                        if (standardFilter.groups && standardFilter.groups.length > 0) {
                            const { excludedPatientIds: _, ...filterWithoutExcluded } = standardFilter;
                            parsedFilterValue = filterWithoutExcluded;
                        }
                    }
                } catch (error) {
                    logger.error('Failed to parse filter_criteria', error);
                }
            }

            // Set patientAdvancedFilter with excluded IDs included
            let finalFilterValue: any = undefined;
            if (parsedFilterValue || excludedPatientIds) {
                finalFilterValue = {
                    ...(parsedFilterValue || {}),
                    ...(excludedPatientIds && excludedPatientIds.length > 0 && { excludedPatientIds }),
                };
                formValues.patientAdvancedFilter = finalFilterValue;
                // Store initial filter for comparison
                initialFilterRef.current = finalFilterValue;

                // Initialize excludedPatientIds state from form (normalize to string[] for "es-only:xxx" support)
                if (excludedPatientIds && excludedPatientIds.length > 0) {
                    setExcludedPatientIds(excludedPatientIds.map((id: string | number) => String(id)));
                }
            }

            // Initialize cohort name
            if (currentCohort?.name) {
                setCohortName(currentCohort.name);
            }

            // Set chat_filter if exists
            const chatFilter = (currentCohort as any)?.chat_filter;
            if (chatFilter) {
                formValues.chat_filter = chatFilter;
            }

            // Set sql_query if exists
            const sqlQuery = currentCohort?.sql_query;
            if (sqlQuery) {
                formValues.sql_query = sqlQuery;
            }

            // Set random selection if exists
            const randomSize = (currentCohort as any)?.random_size;
            const randomSeed = (currentCohort as any)?.random_seed;
            if (randomSize && randomSize > 0) {
                formValues.randomEnabled = true;
                formValues.randomCount = randomSize;
                if (randomSeed) {
                    formValues.randomSeed = String(randomSeed);
                }
            }

            if (Object.keys(formValues).length > 0) {
                // Use setFieldsValue to set form values only once on initial load
                form.setFieldsValue(formValues);
            }

            // Mark as loaded regardless of whether filters exist
            setFiltersLoaded(true);
        });
    }, [currentCohort, form, filtersLoaded]);

    const total = (patientsData?.total as number) || 0;
    const totalUnfiltered = (unfilteredPatientsData?.total as number) || 0;

    // Watch random selection to track filtered patient count (before random selection)
    const randomEnabled = Form.useWatch('randomEnabled', form);
    const randomCount = Form.useWatch('randomCount', form);
    const hasRandomSelection = randomEnabled && typeof randomCount === 'number' && randomCount > 0;

    useEffect(() => {
        queueMicrotask(() => {
            // When random selection is disabled, save the total as filtered patient count
            // This will be used as max for sample size
            // Subtract excluded patient IDs from the total
            if (!randomEnabled && total > 0) {
                const totalExcluded = excludedPatientIds.length;
                setFilteredPatientCount(Math.max(0, total - totalExcluded));
            }
        });
    }, [randomEnabled, total, excludedPatientIds.length]);

    // Update form with randomSeed from backend response to ensure consistency
    // This ensures that the seed used to display patients matches the seed used when saving
    useEffect(() => {
        const seed = (patientsData as any)?.randomSeed;
        if (seed !== undefined && seed !== null && seed !== lastRandomSeedRef.current && randomEnabled) {
            lastRandomSeedRef.current = seed;
            // Always update form with backend seed to ensure consistency
            // This ensures tabloda görünen hastalar ile kaydedilen hastalar aynı seed kullanır
            form.setFieldValue('randomSeed', String(seed));
        }
    }, [(patientsData as any)?.randomSeed, randomEnabled, form]);

    // Use filtered patient count (before random selection) as max for sample size
    // If we have a saved filtered count, use it; otherwise use current total minus excluded IDs
    const totalExcluded = excludedPatientIds.length;
    const maxSampleSize = filteredPatientCount > 0 ? filteredPatientCount : Math.max(0, total - totalExcluded);

    // Get current patient IDs from table
    const currentPatientIds = useMemo(() => {
        return ((patientsData?.patients as any) || []).map((p: any) => String(p.id));
    }, [patientsData?.patients]);

    // Calculate selected row keys (all patients except excluded ones)
    // Default: all checked, unchecking adds to excludedPatientIds (string[] supports "es-only:xxx")
    const selectedRowKeys = useMemo(() => {
        const excludedSet = new Set(excludedPatientIds);
        return currentPatientIds.filter((id: string) => !excludedSet.has(id));
    }, [currentPatientIds, excludedPatientIds]);

    // Row selection configuration
    const rowSelection = useMemo(() => {
        return {
            type: 'checkbox' as const,
            selectedRowKeys,
            onSelect: (record: any, selected: boolean) => {
                const patientId = String(record.id);
                if (selected) {
                    setExcludedPatientIds(prev => prev.filter(id => id !== patientId));
                } else {
                    setExcludedPatientIds(prev => [...prev, patientId]);
                }
            },
            onSelectAll: (selected: boolean, selectedRows: any[], changeRows: any[]) => {
                const changeIds = changeRows.map((r: any) => String(r.id));
                if (selected) {
                    setExcludedPatientIds(prev => prev.filter(id => !changeIds.includes(id)));
                } else {
                    setExcludedPatientIds(prev => Array.from(new Set([...prev, ...changeIds])));
                }
            },
            getCheckboxProps: () => ({
                disabled: hasRandomSelection, // Disable checkboxes when random selection is active
            }),
        };
    }, [selectedRowKeys, hasRandomSelection]);

    // Handle save
    const handleSave = async () => {
        if (!cohort_id) return;

        try {
            const patientFilter = form.getFieldValue('patientAdvancedFilter') as AdvancedFilterQuery | undefined;

            // Helper function to check if filter has meaningful content
            const hasMeaningfulFilter = (filter: any): boolean => {
                if (!filter || typeof filter !== 'object') return false;
                // Check if filter has groups with conditions (excluding excludedPatientIds)
                if (filter.groups && Array.isArray(filter.groups) && filter.groups.length > 0) {
                    return filter.groups.some(
                        (group: any) =>
                            group.conditions && Array.isArray(group.conditions) && group.conditions.length > 0
                    );
                }
                return false;
            };

            // If filter is empty or meaningless, send null to clear filter_criteria
            // But preserve excludedPatientIds if they exist
            let finalPatientFilter: any = null;
            if (hasMeaningfulFilter(patientFilter)) {
                finalPatientFilter = {
                    ...patientFilter,
                    ...(excludedPatientIds.length > 0 && { excludedPatientIds }),
                };
            } else if (excludedPatientIds.length > 0) {
                // If filter is empty but we have excluded IDs, send them in filter
                finalPatientFilter = {
                    excludedPatientIds,
                };
            }

            // Get chat_filter from form
            const formChatFilter = form.getFieldValue('chat_filter');
            const chatFilter = formChatFilter || undefined;

            // Get sql_query from form
            const formSqlQuery = form.getFieldValue('sql_query');

            // Get random selection from form (randomEnabled and randomCount already watched above)
            const randomSeedStr = form.getFieldValue('randomSeed');

            const includeElasticsearchForFilters = watchedIncludeEs === true;
            const updateData: any = {
                patient_filter: finalPatientFilter,
                chat_filter: chatFilter || undefined,
                sql_query: formSqlQuery || undefined,
                include_elasticsearch_for_filters: includeElasticsearchForFilters,
            };

            // Add name if it was edited
            if (cohortName && cohortName !== currentCohort?.name) {
                updateData.name = cohortName;
            }

            // Include random selection if enabled, otherwise clear it from DB
            if (randomEnabled && typeof randomCount === 'number' && randomCount > 0) {
                updateData.random_patients = randomCount;
                if (randomSeedStr && randomSeedStr.trim() !== '') {
                    const parsedSeed = parseInt(randomSeedStr, 10);
                    if (!isNaN(parsedSeed)) {
                        updateData.random_seed = parsedSeed;
                    }
                }
            } else {
                // If randomEnabled is false or not set, clear random selection from DB
                updateData.random_patients = null;
                updateData.random_seed = null;
            }

            await updateCohort({
                cohort_id,
                data: updateData,
            }).unwrap();

            notification.success({
                message: 'Cohort Updated',
                description: 'Cohort filters have been updated successfully.',
                duration: 3,
            });

            navigate(`${Routes.COHORTS}/${cohort_id}`, {
                state: { shouldRefetchPatients: true },
            });
        } catch (error: any) {
            notification.error({
                message: 'Update Failed',
                description: error?.data?.message || 'Failed to update cohort. Please try again.',
                duration: 5,
            });
        }
    };

    if (loadingCohort) {
        return (
            <div className="flex h-screen items-center justify-center">
                <span>Loading cohort...</span>
            </div>
        );
    }

    if (!currentCohort) {
        return (
            <div className="flex h-screen items-center justify-center">
                <span>Cohort not found</span>
            </div>
        );
    }

    return (
        <div className="px-6 pb-0 pt-[8px]">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex-1">
                    {isEditingName ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={cohortName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCohortName(e.target.value)}
                                onPressEnter={() => setIsEditingName(false)}
                                className="!w-[300px] !p-1 !text-[16px] !font-semibold !text-[#434343]"
                                autoFocus
                            />
                            <Button
                                type="text"
                                icon={<CheckOutlined />}
                                onClick={() => setIsEditingName(false)}
                                className="!h-[32px] !text-green-600 hover:!bg-green-50"
                            />
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setCohortName(currentCohort?.name || '');
                                    setIsEditingName(false);
                                }}
                                className="!h-[32px] !text-red-600 hover:!bg-red-50"
                            />
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <h1 className="text-[20px] font-semibold text-[#434343]">
                                {cohortName || currentCohort.name}
                            </h1>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => setIsEditingName(true)}
                                className="!h-[24px] !w-[24px] !p-0 !text-gray-500 hover:!text-[#0098DA]"
                                size="small"
                            />
                        </div>
                    )}
                    <p className="text-[14px] font-normal text-[#626262]">Cohort ID: {currentCohort.cohort_id}</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => navigate(`${Routes.COHORTS}/${cohort_id}`)}
                        className="!h-[32px] rounded-[6px] !text-[12px] font-semibold"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={updatingCohort}
                        className="!h-[32px] rounded-[6px] bg-[#0098DA] !text-[12px] font-semibold hover:!bg-[#007AB8]"
                    >
                        Save Changes
                    </Button>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                className="grid grid-cols-2 gap-x-[20px]"
            >
                <div className="col-span-2 !mt-[20px]">
                    {/* Excluded Patients Info */}
                    {excludedPatientIds.length > 0 && (
                        <div className="mb-4 flex items-center justify-between rounded-[8px] bg-[#FFF4E6] p-3">
                            <div className="flex items-center gap-2">
                                <span className="text-[14px] font-medium text-[#8C5E00]">
                                    {excludedPatientIds.length} patient(s) excluded
                                </span>
                            </div>
                            <Button
                                size="small"
                                onClick={() => setExcludedPatientIds([])}
                                className="!h-[24px] !text-[12px]"
                            >
                                Clear Excluded
                            </Button>
                        </div>
                    )}

                    <PatientListSelection
                        isLazyLoadingPatienList={false}
                        loading={loadingPatients}
                        data={(patientsData?.patients as any) || []}
                        total={totalExcluded ? Math.max(0, total - totalExcluded) : total}
                        totalUnfiltered={totalUnfiltered}
                        rowSelection={rowSelection}
                    />

                    {/* Random Selection UI */}
                    {!watchedIncludeEs && (
                        <Form.Item
                            noStyle
                            shouldUpdate
                        >
                            {({ getFieldValue }: { getFieldValue: (name: string) => any }) => {
                                const enabled = getFieldValue('randomEnabled');

                                return (
                                    <div className="mt-[20px] flex items-center justify-center gap-2 text-[14px] font-medium">
                                        {/* Checkbox */}
                                        <Form.Item
                                            name="randomEnabled"
                                            valuePropName="checked"
                                            noStyle
                                        >
                                            <Checkbox
                                                onChange={(e: CheckboxChangeEvent) => {
                                                    if (!e.target.checked) {
                                                        form.resetFields(['randomCount', 'randomSeed']);
                                                    }
                                                }}
                                            />
                                        </Form.Item>

                                        <span>Sample size (Max {maxSampleSize})</span>

                                        {enabled && (
                                            <Form.Item
                                                name="randomCount"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Enter number of patients',
                                                    },
                                                    {
                                                        type: 'number',
                                                        min: 1,
                                                        max: maxSampleSize,
                                                        message: `Sample size must be between 1 and ${maxSampleSize}`,
                                                    },
                                                ]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    min={1}
                                                    style={{ width: 140 }}
                                                    max={maxSampleSize}
                                                />
                                            </Form.Item>
                                        )}

                                        {!enabled && (
                                            <InputNumber
                                                disabled
                                                style={{ width: 160 }}
                                            />
                                        )}
                                    </div>
                                );
                            }}
                        </Form.Item>
                    )}
                </div>
            </Form>
        </div>
    );
}

/**
 * Edit Cohort Form Page - Main component with provider
 */
export default function EditCohortForm() {
    const { cohort_id } = useParams<{ cohort_id: string }>();
    const { data: cohortsData } = useGetAllCohortsQuery();
    const { setMessageId } = useCohortMessageId();

    // Find current cohort and set messageId if exists
    const currentCohort = useMemo(() => {
        if (!cohortsData?.cohorts || !cohort_id) return null;
        return cohortsData.cohorts.find(c => c.cohort_id === cohort_id);
    }, [cohortsData, cohort_id]);

    // Set messageId from cohort if it exists
    useEffect(() => {
        if (currentCohort?.message_id) {
            setMessageId(currentCohort.message_id);
        }
    }, [currentCohort?.message_id, setMessageId]);

    return (
        <CohortFormProvider>
            <EditCohortFormContent />
        </CohortFormProvider>
    );
}
